import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition } from '~/consts/agentHub';

import { useListAgencies } from '~/hooks/apis/agent/useListAgencies';

const DEFAULT_SIZE_PER_PAGE = '20';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function AgentHubCommissionsAgencyInput({ setCreatedConditions, existingConditions }: Props) {
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAgencies, setSelectedAgencies] = useState<CommissionOption>(existingConditions || []);

  const { data, isFetching } = useListAgencies(
    {
      page: '0',
      pageSize: DEFAULT_SIZE_PER_PAGE,
      search: debouncedSearch,
    },
    { enabled: debouncedSearch.length > 0 },
  );

  const result = useMemo(() => {
    if (!data?.rows) {
      return [];
    }
    return data.rows.map((agency) => ({ value: agency.id, label: agency.name }));
  }, [data]);

  const noOptionsText = useMemo(() => {
    if (!debouncedSearch) {
      return 'Start typing to search for agencies';
    }
    if (data?.total === 0) {
      return 'No agencies found';
    }
    return 'Start typing to search for agencies';
  }, [debouncedSearch, data?.total]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 500),
    [],
  );

  const handleSaveCondition = () => {
    setCreatedConditions(
      {
        type: 'agency',
        value: selectedAgencies,
      },
      true,
    );
  };

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (newInputValue) {
      debouncedSetSearch(newInputValue);
    }
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedAgencies(value);
  };

  useEffect(() => {
    handleSaveCondition();
  }, [selectedAgencies]);

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={isFetching}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label="Agencies" placeholder={selectedAgencies.length > 0 ? '' : 'Agencies'} />
          )}
          onInputChange={handleInputChange}
          value={selectedAgencies}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for agencies
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsAgencyInput;
